import { useState, useEffect, useCallback } from 'react';
import FilesApi, { FileResponseDto, UploadFileDto } from '@/api/files';

export const useDocuments = (userId?: number | string | null) => {
  const [documents, setDocuments] = useState<FileResponseDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const params = { category: 'attachment' as const, entityType: 'user' as const };
      const response = userId
        ? await FilesApi.getFilesByUserId(Number(userId), params)
        : await FilesApi.getMyFiles(params);

      setDocuments(response.data.files);
      setError(null);
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleUpload = useCallback(async (file: File) => {
    return new Promise<void>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64Content = reader.result as string;
          const uploadData: UploadFileDto = {
            content: base64Content,
            originalName: file.name,
            category: 'attachment',
            entityType: 'user',
            isPublic: false,
          };

          if (userId) {
            await FilesApi.uploadFileForUser(Number(userId), uploadData);
          } else {
            await FilesApi.uploadFile(uploadData);
          }
          
          await loadDocuments(); // Refresh list
          resolve();
        } catch (uploadError) {
          console.error('Error uploading file:', uploadError);
          setError('Failed to upload file');
          reject(uploadError);
        }
      };
      reader.onerror = () => {
        setError('Failed to read file');
        reject(reader.error);
      };
      reader.readAsDataURL(file);
    });
  }, [userId, loadDocuments]);

  const handleUpdate = useCallback(async (id: number, newName: string) => {
    try {
      await FilesApi.updateFileName(id, newName);
      await loadDocuments(); // Refresh list
    } catch (updateError) {
      console.error('Error updating file name:', updateError);
      setError('Failed to update file name');
    }
  }, [loadDocuments]);

  const handleDelete = useCallback(async (id: number) => {
    try {
      await FilesApi.deleteFile(id);
      await loadDocuments(); // Refresh list
    } catch (deleteError) {
      console.error('Error deleting document:', deleteError);
      setError('Failed to delete document');
    }
  }, [loadDocuments]);

  return {
    documents,
    loading,
    error,
    onUpload: handleUpload,
    onUpdate: handleUpdate,
    onDelete: handleDelete,
  };
};
